import * as React from 'react';

import { SearchResult } from 'features/search/types';
import { HeadlineLevel, LOCALE, SITE, SiteId } from 'features/ui/enums';
import { capitalizeFirstLetter } from 'features/services/utils/helpers';
import { renderInhabitDate } from 'features/services/utils/date-helpers';
import { DisposalForm, ObjectStatus, ResidenceType, StageStatus } from 'features/search/filters/enums';
import { cleanFinnishNames, getDisposalFormName, getMunicipalityName } from 'features/services/utils/name-helpers';
import { getCurrentLocale, getCurrentSite, getLocalizedKvm, getLocalizedPrice, getLocalizedRok } from 'features/services/utils/env-helper';

import { P } from '@atoms/Text/P';
import { LogoBoratt } from '@atoms/Media/Logo/_logo-boratt';
import { SiteSpecificContent } from '@atoms/SiteSpecificContent';
import { LogoSeniorgarden } from '@atoms/Media/Logo/_logo-seniorgarden';
import { Icon } from '@atoms/Media/Icon';
import { ResidenceUsp } from './_residenceUsp';
import { FormattedMessage } from 'react-intl';
import { Margin } from 'features/ui/enums';
import { ContentTemplate } from './_contentTemplate';
import { getFormattedMessage } from 'features/services/utils/language-helper';

interface Props {
    residence: SearchResult;
    level?: HeadlineLevel;
}

export const ObjectContent: React.FunctionComponent<Props> = ({ residence, level }) => {
    const locale = getCurrentLocale();
    const currentSite = getCurrentSite();
    let price = '';
    let description = '';
    const showPrice = residence.objectStatus !== ObjectStatus.SOLD && residence.objectStatus !== ObjectStatus.BOOKED;

    const getResidenceTitle =() =>{
        if(getCurrentSite() === SITE.FI && typeof residence.jmoyNumberOfRoomsExtraInfo !== 'undefined' && residence.jmoyNumberOfRoomsExtraInfo !== null ) {
            return `${residence.jmoyNumberOfRoomsExtraInfo}, ${getLocalizedKvm(residence.primaryArea)}`
        }

        if(getCurrentSite() === SITE.NO && residence.jmnoBraInternal !== null && residence.jmnoBraInternal !== undefined) {
            return `${getLocalizedRok(residence.rooms)}, ${getLocalizedKvm(residence.jmnoBraInternal)}`;
        }

        if (residence.residenceType == ResidenceType.PLOT) {
            return `${getLocalizedKvm(residence.plotArea)}`;
        }

        return `${getLocalizedRok(residence.rooms)}, ${getLocalizedKvm(residence.primaryArea)}`;
    };

    const getTotalPrice = () => {
        let price = residence.price;

        if(residence.otherExpenses) {
            price += residence.otherExpenses;
        }

        if(residence.jmnoFellesgjeld && residence.residenceType === ResidenceType.APARTMENT && (residence.disposalForm === DisposalForm.OWNERSHIP || residence.disposalForm === DisposalForm.OWNERSHIP_APARTMENT)) {
            price += residence.jmnoFellesgjeld;
        }

        return getLocalizedPrice(price);
    };
    const getSecondPrice = () => {
        if(residence.objectStatus === ObjectStatus.SOLD || residence.objectStatus == ObjectStatus.BOOKED) {
            return price;
        }
        
        if(residence.jmoyTotalPrice && locale === LOCALE.FI) {
            price += getLocalizedPrice(residence.jmoyTotalPrice);
        }
        
        if(locale === LOCALE.NO) {
            price += getTotalPrice();
        }

        return price;
    };
    
    const shouldRenderFloor = () => {
        return (residence.floor !== null && typeof residence.floor !== 'undefined' &&
                residence.maxFloor !== 0 && residence.maxFloor !== null && typeof residence.maxFloor !== 'undefined');
    };
    const getDescription = () => {
        if(locale !== LOCALE.FI) {
            description += getDisposalFormName(residence.disposalForm as DisposalForm);

            if(shouldRenderFloor()) description += ' • ';
        }

        if(shouldRenderFloor()) {
            description += getFormattedMessage('jm.components.ObjectCard.floor') + ' ';
            description += residence.floor + '/' + residence.maxFloor;

            if(locale === LOCALE.FI) description += ' • ';
        }

        if(residence.name && locale === LOCALE.FI) {
            description += 'Asunto ' + residence.name.toUpperCase();
        }

        return description;
    }
    
    return (
        <ContentTemplate 
            residence={residence} 
            headline={getResidenceTitle()} 
            firstPrice={showPrice
                ? getLocalizedPrice(residence.price)
                : ''
            } 
            secondPrice={getSecondPrice()}
            description={getDescription()}
            level={level}
        />
    )
}